import axios from 'axios';
import { 
  SearchCafesResponse, 
  GeocodeResponse, 
  SaveVisitResponse, 
  SaveVisitRequest,
  PendingRatingsResponse,
  SubmitRatingResponse,
  CafeVisit,
  RecentReviewsResponse
} from '../types/explore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const exploreApi = {
  // Search for cafes near location
  searchCafes: async (data: {
    lat: number;
    lng: number;
    address?: string;
    radius?: number;
  }): Promise<SearchCafesResponse> => {
    const response = await api.post('/api/explore/search', data);
    return response.data;
  },

  // Convert address to coordinates
  geocodeAddress: async (address: string): Promise<GeocodeResponse> => {
    const response = await api.post('/api/explore/geocode', { address });
    return response.data;
  },

  // Save a cafe visit - UPDATED to use SaveVisitRequest
  saveVisit: async (data: SaveVisitRequest): Promise<SaveVisitResponse> => {
    const response = await api.post('/api/explore/visits', data);
    return response.data;
  },

  // Get pending ratings
  getPendingRatings: async (): Promise<PendingRatingsResponse> => {
    const response = await api.get('/api/explore/visits/pending');
    return response.data;
  },

  // Submit a rating
  submitRating: async (visitId: string, data: {
    rating: number;
    feedback?: string;
  }): Promise<SubmitRatingResponse> => {
    console.log('📤 Submitting rating for visit:', visitId, data);
    
    try {
      const response = await api.post(`/api/explore/visits/${visitId}/rate`, data);
      console.log('✅ Rating submission response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Rating submission error:', error.response?.data || error.message);
      throw error;
    }
  },

  // Get user's visit history
  getVisitHistory: async (params?: {
    limit?: number;
    page?: number;
  }): Promise<{ success: boolean; visits: CafeVisit[] }> => {
    const response = await api.get('/api/explore/visits/history', { params });
    return response.data;
  },

  getRecentReviews: async (): Promise<RecentReviewsResponse> => {
    try {
      const response = await api.get('/api/explore/reviews/recent');
      return response.data;
    } catch (error: any) {
      console.error('Error fetching recent reviews:', error.response?.data || error.message);
      throw error;
    }
  },
};

// Admin API
export const adminApi = {
  // Initialize admin system
  initializeAdminSystem: async () => {
    const response = await api.get('/api/admin/init');
    return response.data;
  },

  // Get all admin users
  getAdminUsers: async () => {
    const response = await api.get('/api/admin/admins');
    return response.data;
  },

  // Add user to admin list
  addAdmin: async (userId: string) => {
    const response = await api.post('/api/admin/admins/add', { userId });
    return response.data;
  },

  // Remove user from admin list
  removeAdmin: async (userId: string) => {
    const response = await api.delete(`/api/admin/admins/remove/${userId}`);
    return response.data;
  },

  // Check if current user is admin
  checkAdminStatus: async () => {
    try {
      const response = await api.get('/api/admin/admins');
      return response.data;
    } catch (error) {
      console.error('Admin status check error:', error);
      return { success: false, admins: [], adminIds: [] };
    }
  }
};

export default api;
